import * as THREE from 'three';
import { SceneManager } from './SceneManager';
import { CameraManager } from './CameraManager';
import { RendererService } from './RendererService';
import { CloudSystem } from './CloudSystem';
import { VegetationSystem } from './VegetationSystem';

/**
 * Main manager class for Three.js integration
 * Handles initialization, rendering, and cleanup of the 3D scene
 */
export class ThreeJSManager {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private sceneManager: SceneManager;
  private cameraManager: CameraManager;
  private rendererService: RendererService;
  private cloudSystem: CloudSystem;
  private vegetationSystem: VegetationSystem;
  private clock: THREE.Clock;
  private isDisposed: boolean = false;

  /**
   * Creates a new ThreeJSManager instance
   * @param canvas The HTML canvas element to render to
   */
  constructor(canvas: HTMLCanvasElement) {
    // Initialize clock for time-based animations
    this.clock = new THREE.Clock();

    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x87ceeb); // Sky blue background

    // Create camera
    this.camera = new THREE.PerspectiveCamera(
      75, // Field of view
      window.innerWidth / window.innerHeight, // Aspect ratio
      0.1, // Near clipping plane
      1000 // Far clipping plane
    );
    this.camera.position.set(0, 5, 10);
    this.camera.lookAt(0, 0, 0);

    // Create renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Initialize managers
    this.sceneManager = new SceneManager(this.scene);
    this.cameraManager = new CameraManager(this.camera);
    this.rendererService = new RendererService(this.renderer);
    
    // Initialize cloud system
    this.cloudSystem = new CloudSystem(this.scene);
    
    // Initialize vegetation system
    this.vegetationSystem = new VegetationSystem(this.scene);

    // Add basic lighting
    this.setupLighting();

    // Add window resize handler
    this.handleResize();
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  /**
   * Sets up basic lighting for the scene
   */
  private setupLighting(): void {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);

    // Directional light (sun)
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(50, 50, 50);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 500;
    directionalLight.shadow.camera.left = -100;
    directionalLight.shadow.camera.right = 100;
    directionalLight.shadow.camera.top = 100;
    directionalLight.shadow.camera.bottom = -100;
    this.scene.add(directionalLight);
  }

  /**
   * Handles window resize events
   */
  private handleResize(): void {
    // Update camera aspect ratio
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();

    // Update renderer size
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  /**
   * Updates the scene and renders a frame
   */
  public update(): void {
    if (this.isDisposed) return;

    // Get delta time
    const deltaTime = this.clock.getDelta();

    // Update scene manager
    this.sceneManager.update(deltaTime);
    
    // Update cloud system
    this.cloudSystem.update(deltaTime);
    
    // Update vegetation system
    this.vegetationSystem.update(deltaTime);

    // Render scene
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Disposes of resources
   */
  public dispose(): void {
    if (this.isDisposed) return;
    this.isDisposed = true;

    console.log("Disposing ThreeJSManager");

    // Remove event listeners
    window.removeEventListener('resize', this.handleResize.bind(this));

    // Dispose of managers
    this.sceneManager.dispose();
    // The CameraManager doesn't have a dispose method
    this.rendererService.dispose();
    
    // Dispose of cloud system
    this.cloudSystem.dispose();
    
    // Dispose of vegetation system
    this.vegetationSystem.dispose();

    // Dispose of renderer
    this.renderer.dispose();

    // Clear references
    // @ts-ignore
    this.scene = null;
    // @ts-ignore
    this.camera = null;
    // @ts-ignore
    this.renderer = null;
  }

  /**
   * Gets the current scene
   * @returns The Three.js scene
   */
  public getScene(): THREE.Scene {
    return this.scene;
  }

  /**
   * Gets the current camera
   * @returns The Three.js camera
   */
  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  /**
   * Gets the current renderer
   * @returns The Three.js renderer
   */
  public getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  /**
   * Gets the scene manager
   * @returns The scene manager
   */
  public getSceneManager(): SceneManager {
    return this.sceneManager;
  }

  /**
   * Gets the camera manager
   * @returns The camera manager
   */
  public getCameraManager(): CameraManager {
    return this.cameraManager;
  }
} 